/*
 * Copyright 2026 Sony Corporation
 */

const CACHE = 'asset-bytes-v1';

/** Absent in jsdom, and in any context the browser does not consider secure. */
const open = async (): Promise<Cache | undefined> =>
  typeof caches === 'undefined' ? undefined : caches.open(CACHE).catch(() => undefined);

/** The Cache API refuses any scheme but http(s), so a bare key such as `library:<id>` is wrapped in one. */
const requestFor = (key: string): string =>
  /^https?:\/\//.test(key) ? key : `https://asset-cache.local/${encodeURIComponent(key)}`;

/** A hit, or nothing; never a download. */
export const peekBlob = async (key: string): Promise<Blob | undefined> => {
  const store = await open();
  const hit = await store?.match(requestFor(key)).catch(() => undefined);
  return hit?.blob();
};

export const putBlob = async (key: string, blob: Blob): Promise<void> => {
  const store = await open();
  await store?.put(requestFor(key), new Response(blob)).catch(() => undefined);
};

export const dropBlob = async (key: string): Promise<void> => {
  const store = await open();
  await store?.delete(requestFor(key)).catch(() => false);
};

/**
 * Bytes kept across reloads, under a key that names the asset.
 *
 * The key is given rather than taken from the response because the URL a model
 * arrives on is often signed: a different string every time, which as a cache
 * key would only ever record misses. What identifies the bytes is the asset,
 * so `download` may go anywhere as long as the key does not move.
 *
 * The store is an optimisation and never a requirement — a browser that
 * refuses to open it, or one already at quota, still gets the download.
 */
export const cachedBlob = async (key: string, download: () => Promise<Blob>): Promise<Blob> => {
  const hit = await peekBlob(key);
  if (hit) {
    return hit;
  }

  const blob = await download();
  await putBlob(key, blob);
  return blob;
};

const sceneKey = (sceneId: string, revision: number | '') => `scene:${sceneId}@${revision}`;

/** Only the newest revision is ever loaded again, and each can be 100 MB, so the older ones go. */
export const putSceneRevision = async (sceneId: string, revision: number, blob: Blob): Promise<void> => {
  const store = await open();
  if (!store) {
    return;
  }
  const older = requestFor(sceneKey(sceneId, ''));
  for (const request of await store.keys().catch(() => [])) {
    if (request.url.startsWith(older)) {
      await store.delete(request).catch(() => false);
    }
  }
  await store.put(requestFor(sceneKey(sceneId, revision)), new Response(blob)).catch(() => undefined);
};

/** A published revision is immutable, so its bytes are good for as long as it is the one worlds render. */
export const cachedSceneRevision = async (
  sceneId: string,
  revision: number,
  download: () => Promise<Blob>
): Promise<Blob> => {
  const hit = await peekBlob(sceneKey(sceneId, revision));
  if (hit) {
    return hit;
  }

  const blob = await download();
  await putSceneRevision(sceneId, revision, blob);
  return blob;
};

/** For a URL that names its content, which is its own key. */
export const fetchCached = async (url: string, signal?: AbortSignal): Promise<Blob> =>
  cachedBlob(url, async () => {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`${url} could not be downloaded (${String(response.status)}).`);
    }
    return response.blob();
  });
